import React, { useState, useEffect, useRef } from 'react';
import './WeatherAlerts.css';

const WeatherAlerts = ({ alerts, onAlertClick }) => {
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [alertDetails, setAlertDetails] = useState({});
  const [fetchedAlerts, setFetchedAlerts] = useState(new Set());
  
  // Handle different potential data structures
  const getAlertDetailInfo = () => {
    if (!alerts) return [];
    
    // Structure: alerts.alertDetailInfo array
    if (Array.isArray(alerts.alertDetailInfo)) {
      console.log('Found alerts in alerts.alertDetailInfo');
      return alerts.alertDetailInfo;
    }
    // Structure: alerts.alerts array
    else if (alerts.alerts && Array.isArray(alerts.alerts)) {
      console.log('Found alerts in alerts.alerts');
      return alerts.alerts;
    }
    // Structure: alerts is the array itself
    else if (Array.isArray(alerts)) {
      console.log('Alerts is already an array');
      return alerts;
    }
    
    console.log('No alerts found in any expected location', alerts);
    return [];
  };
  
  // Initial fetch effect - runs once on component mount to pre-fetch all alert details
  useEffect(() => {
    const fetchInitialAlertDetails = () => {
      const alertList = getAlertDetailInfo();
      if (alertList && alertList.length > 0) {
        console.log('Initial pre-fetch of alert details');
        
        // Batch all the alert IDs to fetch to avoid state update issues
        const alertsToFetch = [];
        
        alertList.forEach(alert => {
          if (alert.detailKey && onAlertClick && !fetchedAlerts.has(alert.detailKey)) {
            alertsToFetch.push(alert.detailKey);
          }
        });
        
        // Add all to the fetched set at once
        if (alertsToFetch.length > 0) {
          setFetchedAlerts(prev => {
            const newSet = new Set(prev);
            alertsToFetch.forEach(id => newSet.add(id));
            return newSet;
          });
          
          // Fetch each alert detail
          alertsToFetch.forEach(id => {
            console.log(`Fetching initial details for alert: ${id}`);
            onAlertClick(id);
          });
        }
      }
    };
    
    // Run the fetch immediately
    fetchInitialAlertDetails();
    // This effect should only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Effect to handle new alerts if they arrive after initial mount
  useEffect(() => {
    const alertList = getAlertDetailInfo();
    if (alertList && alertList.length > 0) {
      // Check if there are any new alerts we haven't fetched yet
      const newAlerts = alertList.filter(alert => 
        alert.detailKey && !fetchedAlerts.has(alert.detailKey)
      );
      
      if (newAlerts.length > 0) {
        console.log(`Fetching details for ${newAlerts.length} new alerts`);
        
        // Add all to the fetched set at once
        setFetchedAlerts(prev => {
          const newSet = new Set(prev);
          newAlerts.forEach(alert => newSet.add(alert.detailKey));
          return newSet;
        });
        
        // Fetch each new alert's details
        newAlerts.forEach(alert => {
          console.log(`Fetching details for new alert: ${alert.detailKey}`);
          onAlertClick(alert.detailKey);
        });
      }
    }
  }, [alerts, onAlertClick, fetchedAlerts]);
  
  const alertList = getAlertDetailInfo();
  console.log('Alert list for display:', alertList);
  
  // If no alerts, don't render anything
  if (!alertList || alertList.length === 0) {
    console.log('No alerts to display');
    return null;
  }
  
  // Sort alerts by severity code (lower numbers are more severe)
  const sortedAlerts = [...alertList].sort((a, b) => {
    return (a.severityCode || 5) - (b.severityCode || 5);
  });
  
  // Get severity color based on severity code
  const getSeverityColor = (severityCode) => {
    switch (parseInt(severityCode)) {
      case 1: // Extreme
        return '#c41e3a'; // Deep Red
      case 2: // Severe
        return '#da5d00'; //  Red/Orange
      case 3: // Moderate
        return '#cd9700'; // Orange
      case 4: // Minor
        return '#af9e00cc'; // Yellow
      default:
        return '#000000'; // White for unknown
    }
  };
  
  // Get alert type as a slug for data attribute
  const getAlertTypeSlug = (phenomena, eventDescription) => {
    if (eventDescription) {
      return eventDescription.toLowerCase().replace(/\s+/g, '-');
    }
    
    // Map of phenomena codes to type slugs
    const typeMap = {
      'TO': 'tornado',
      'FL': 'flood',
      'FF': 'flash-flood',
      'SV': 'severe-thunderstorm',
      'HU': 'hurricane',
      'TS': 'thunderstorm',
      'WS': 'winter-storm',
      'BZ': 'blizzard',
      'WW': 'winter-weather',
      'HT': 'heat',
      'FW': 'fire-weather',
      'HF': 'high-fire-danger',
      'HW': 'high-wind',
      'WI': 'wind',
      'DU': 'dust',
      'FG': 'dense-fog',
      'SM': 'smoke',
      'AF': 'ashfall',
      'EC': 'extreme-cold',
      'EH': 'excessive-heat',
      'UP': 'unknown-precipitation',
      'HE': 'hydrologic',
      'FA': 'areal-flood',
      'TWA': 'winter-storm-watch'
    };
    
    return typeMap[phenomena] || 'weather-alert';
  };
  
  // Get alert icon based on type/phenomena
  const getAlertIcon = (phenomena) => {
    // Default icon
    const defaultIcon = '⚠️';
    
    // Map of phenomena codes to appropriate icons
    const iconMap = {
      'TO': '🌪️', // Tornado
      'FL': '🌊', // Flood
      'FF': '🌊', // Flash Flood
      'SV': '⛈️', // Severe Thunderstorm
      'HU': '🌀', // Hurricane
      'TS': '⛈️', // Thunderstorm
      'WS': '❄️', // Winter Storm
      'BZ': '❄️', // Blizzard
      'WW': '❄️', // Winter Weather
      'HT': '🔥', // Heat
      'FW': '🔥', // Fire Weather
      'HF': '🔥', // High Fire Danger
      'HW': '🌡️', // High Wind
      'DU': '💨', // Dust
      'FG': '🌫️', // Dense Fog
      'SM': '🌫️', // Smoke
      'AF': '🐎', // Ashfall
      'WI': '💨', // Wind
      'EC': '❄️', // Extreme Cold
      'EH': '🌡️', // Excessive Heat
      'UP': '🌀', // Unknown Precipitation
      'HE': '💧', // Hydrologic
      'FA': '🌊', // Areal Flood
      'TWA': '❄️', // Winter Storm Watch
    };
    
    return iconMap[phenomena] || defaultIcon;
  };
  
  const toggleAlertDetail = (alertId) => {
    if (expandedAlertId === alertId) {
      setExpandedAlertId(null);
    } else {
      setExpandedAlertId(alertId);
      // We still call onAlertClick here in case there's additional logic or display updates needed
      if (onAlertClick) {
        // Add to the set of fetched alerts to prevent duplicate fetching
        if (!fetchedAlerts.has(alertId)) {
          setFetchedAlerts(prev => new Set([...prev, alertId]));
          onAlertClick(alertId);
        }
      }
    }
  };

  // Convert URLs to clickable links in text
  const createMarkup = (text) => {
    if (!text) return { __html: '' };
    
    // Regular expression to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const htmlText = text.replace(urlRegex, url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    
    return { __html: htmlText };
  };
  
  // Get the description text from alertDetail or alert.texts
  const getDescriptionText = (alert) => {
    // If we have alertDetails with texts array from the detail API
    if (alertDetails[alert.detailKey] && 
        alertDetails[alert.detailKey].texts && 
        alertDetails[alert.detailKey].texts.length > 0) {
      
      const texts = alertDetails[alert.detailKey].texts;
      // Find English description or use the first one
      const englishText = texts.find(t => t.languageCode === 'en-US') || texts[0];
      if (englishText && englishText.description) {
        return englishText.description;
      }
    }
    
    // If the alert itself has texts
    if (alert.texts && alert.texts.length > 0) {
      const englishText = alert.texts.find(t => t.languageCode === 'en-US') || alert.texts[0];
      if (englishText && englishText.description) {
        return englishText.description;
      }
    }
    
    // Fallback to descriptionText if it exists
    return alert.descriptionText || '';
  };
  
  // Get the instruction text
  const getInstructionText = (alert) => {
    // If we have alertDetails with texts array from the detail API
    if (alertDetails[alert.detailKey] && 
        alertDetails[alert.detailKey].texts && 
        alertDetails[alert.detailKey].texts.length > 0) {
      
      const texts = alertDetails[alert.detailKey].texts;
      // Find English instruction or use the first one
      const englishText = texts.find(t => t.languageCode === 'en-US') || texts[0];
      if (englishText && englishText.instruction) {
        return englishText.instruction;
      }
    }
    
    // If the alert itself has texts
    if (alert.texts && alert.texts.length > 0) {
      const englishText = alert.texts.find(t => t.languageCode === 'en-US') || alert.texts[0];
      if (englishText && englishText.instruction) {
        return englishText.instruction;
      }
    }
    
    // Fallback to instructionText if it exists
    return alert.instructionText || '';
  };
  
  // When alertDetail is updated from parent
  useEffect(() => {
    if (alerts && alerts.alertDetail) {
      const detailKey = alerts.alertDetail.detailKey;
      // Update alert details in state
      setAlertDetails(prevDetails => ({
        ...prevDetails,
        [detailKey]: alerts.alertDetail
      }));
      
      // Also mark this alert as fetched
      if (detailKey && !fetchedAlerts.has(detailKey)) {
        setFetchedAlerts(prev => new Set([...prev, detailKey]));
      }
    }
  }, [alerts, fetchedAlerts]);
  
  return (
    <div className="weather-alerts-container">
      {sortedAlerts.map(alert => (
        <div 
          key={alert.detailKey} 
          className={`weather-alert ${expandedAlertId === alert.detailKey ? 'expanded' : ''}`}
          data-alert-type={getAlertTypeSlug(alert.phenomena, alert.eventDescription)}
        >
          <div 
            className="alert-header"
            style={{ backgroundColor: getSeverityColor(alert.severityCode) }}
            onClick={() => toggleAlertDetail(alert.detailKey)}
          >
            <div className="alert-icon">{getAlertIcon(alert.phenomena)}</div>
            <div className="alert-headline">{alert.headlineText}</div>
            <div className="alert-toggle">{expandedAlertId === alert.detailKey ? '▲' : '▼'}</div>
          </div>
          
          {expandedAlertId === alert.detailKey && (
            <div className="alert-details">
              <div className="alert-meta">
                <span className="alert-severity">Severity: {alert.severity}</span>
                <span className="alert-source">Source: {alert.source}</span>
              </div>
              
              {getDescriptionText(alert) && (
                <div className="alert-description">
                  <h4>Description:</h4>
                  <p dangerouslySetInnerHTML={createMarkup(getDescriptionText(alert))}></p>
                </div>
              )}
              
              {getInstructionText(alert) && (
                <div className="alert-instructions">
                  <h4>Instructions:</h4>
                  <p dangerouslySetInnerHTML={createMarkup(getInstructionText(alert))}></p>
                </div>
              )}
              
              <div className="alert-timing">
                <p>Effective: {new Date(alert.effectiveTimeLocal).toLocaleString()}</p>
                <p>Expires: {new Date(alert.expireTimeLocal).toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default WeatherAlerts; 